import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { jobsApi } from '../services/api';
import { pushToast } from '../store/slices/uiSlice';

const POLL_MS = 2000;
const TIMEOUT_MS = 90000;

/**
 * Kuyruğa alınan bir hesaplama işini (dönem hesabı, deneme hesabı, görev
 * yeniden hesaplaması) bitene kadar yoklar; bitince `onDone(job)` çağrılır ve
 * sonuç bildirilir. Hesaplama worker'da arka planda çalıştığı için sayfalar
 * aksi halde yenilenene kadar eski veriyle kalıyordu (2026-09-21).
 *
 * Döndürdüğü fonksiyon: `watch(jobId, { label, onDone })` — `label` bildirim
 * metninin başı (ör. `"Eylül 2026" hesaplaması`). Sayfadan çıkılınca yoklama
 * durur; 90 saniyede bitmezse `onDone(null)` çağrılıp bilgi verilir.
 */
export default function useJobWatcher() {
  const dispatch = useDispatch();
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return useCallback(
    async (jobId, { label = 'Hesaplama', onDone } = {}) => {
      if (!jobId) return;
      const deadline = Date.now() + TIMEOUT_MS;
      while (mountedRef.current && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, POLL_MS));
        if (!mountedRef.current) return;
        let job = null;
        try {
          job = (await jobsApi.get(jobId))?.data;
        } catch (_e) {
          continue; // geçici hata — bir sonraki turda tekrar dene
        }
        if (!job?.done) continue;
        onDone?.(job);
        if (job.status === 'SUCCESS') {
          dispatch(pushToast({ kind: 'success', message: `${label} tamamlandı.` }));
        } else if (job.status === 'PARTIAL_FAILED') {
          dispatch(pushToast({ kind: 'warn', message: `${label} tamamlandı, bazı görevlerde hata var.` }));
        } else {
          dispatch(pushToast({
            kind: 'error',
            message: `${label} başarısız${job.error ? `: ${job.error}` : '.'}`,
          }));
        }
        return;
      }
      if (mountedRef.current) {
        onDone?.(null);
        dispatch(pushToast({ kind: 'info', message: 'Hesaplama sürüyor; sonuçlar birazdan yansır.' }));
      }
    },
    [dispatch],
  );
}
